/**
 * Rapporteur SVG partagé : rendu graduated (corps + chiffres + ticks + poignée).
 *
 * Utilisé par :
 *  - `RapporteurInteractifElementEleve` (élément additionnel "mesurer un angle")
 *  - `ProgrammeConstructionEleve` pour les tasks `kind: 'angle_ray'` ("construire un angle")
 *
 * Convention d'angle : sens mathématique standard — 0° = horizontale droite,
 * sens trigo (anti-horaire). Le y SVG est inversé (axe y descendant) donc on
 * applique `-angleDeg` dans les `Math.cos/sin`.
 */

// ─── Helpers mathématiques ────────────────────────────────────────────
export const toRad = (d) => (d * Math.PI) / 180;
export const toDeg = (r) => (r * 180) / Math.PI;

/** Point en coords SVG à distance `r` du centre `(cx, cy)` dans la direction `angleDeg`. */
export const polarPoint = (cx, cy, r, angleDeg) => ({
    x: cx + r * Math.cos(toRad(-angleDeg)),
    y: cy + r * Math.sin(toRad(-angleDeg)),
});

/** Normalise un angle en degrés dans [0, 360). */
export const normDeg = (d) => ((d % 360) + 360) % 360;

/** Snap d'une valeur sur un pas (arrondi). */
export const snapTo = (val, step) => Math.round(val / step) * step;

// ─── Composant ProtractorSvg ──────────────────────────────────────────

/**
 * @param {number} cx, cy          — centre du rapporteur (coords SVG)
 * @param {number} rotation        — rotation en degrés (sens math)
 * @param {number} radius          — rayon du corps (px)
 * @param {number} [readAngle]     — graduation à surligner en rouge (lecture)
 * @param {number} [highlighted]   — graduation à mettre en évidence (cible de clic)
 * @param {'dark'|'light'} [theme]
 */
export const ProtractorSvg = ({
    cx, cy, rotation, radius,
    readAngle = null, highlighted = null, theme = 'dark',
}) => {
    const textMain = '#1e3a5f';
    const textDim = theme === 'light' ? '#64748b' : '#94a3b8';
    const ticks = [];
    for (let i = 0; i <= 180; i++) {
        const isLong = i % 10 === 0;
        const isMed = i % 5 === 0 && !isLong;
        const len = isLong ? 18 : isMed ? 12 : 7;
        const inner = radius - len;
        const a = toRad(-i);
        const x1 = Math.cos(a) * inner;
        const y1 = Math.sin(a) * inner;
        const x2 = Math.cos(a) * radius;
        const y2 = Math.sin(a) * radius;
        const isRead = readAngle === i;
        const isTarget = highlighted === i;
        ticks.push(
            <line
                key={`t${i}`}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={isRead ? '#ef4444' : isTarget ? '#16a34a' : isLong ? '#334155' : (theme === 'light' ? '#64748b' : '#94a3b8')}
                strokeWidth={isRead || isTarget ? 2.5 : isLong ? 1.2 : 0.6}
            />
        );
        if (isLong) {
            const lx = Math.cos(a) * (radius - 24);
            const ly = Math.sin(a) * (radius - 24);
            ticks.push(
                <text
                    key={`lo${i}`} x={lx} y={ly}
                    textAnchor="middle" dominantBaseline="central"
                    fontSize={9} fontWeight={600}
                    fill={isTarget ? '#16a34a' : textMain}
                    fontFamily="system-ui, sans-serif"
                >
                    {i}
                </text>
            );
            {
                // L'échelle intérieure va de 180 à 0, ses deux extrémités
                // COMPRISES : sans le 180 au-dessus du 0 et le 0 au-dessus du
                // 180, l'élève qui mesure depuis l'autre côté n'a aucun zéro à
                // aligner sur son premier côté, et lit le complément.
                // À ces deux angles seulement, les deux nombres tomberaient sur
                // l'horizontale à 11 px l'un de l'autre : on décale celui de
                // l'échelle intérieure vers le corps du rapporteur.
                const auBord = (i === 0 || i === 180);
                const lx2 = Math.cos(a) * (radius - 35);
                const ly2 = Math.sin(a) * (radius - 35) - (auBord ? 9 : 0);
                ticks.push(
                    <text
                        key={`li${i}`} x={lx2} y={ly2}
                        textAnchor="middle" dominantBaseline="central"
                        fontSize={7.5} fontWeight={500} fill={textDim}
                        fontFamily="system-ui, sans-serif"
                    >
                        {180 - i}
                    </text>
                );
            }
        }
    }

    // Ligne rouge qui marque la graduation lue
    let readLine = null;
    if (readAngle !== null && readAngle !== undefined) {
        const a = toRad(-readAngle);
        readLine = (
            <line
                x1={0} y1={0}
                x2={Math.cos(a) * (radius + 5)} y2={Math.sin(a) * (radius + 5)}
                stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.85}
            />
        );
    }

    return (
        <g transform={`translate(${cx},${cy}) rotate(${-rotation})`}>
            {/* Socle */}
            <path
                d={`M ${-radius - 4} 3 A ${radius + 4} ${radius + 4} 0 0 1 ${radius + 4} 3 L ${radius + 4} 6 L ${-radius - 4} 6 Z`}
                fill="rgba(200,220,255,0.25)" stroke="#93c5fd" strokeWidth={1}
            />
            {/* Corps semi-circulaire */}
            <path
                d={`M ${-radius} 0 A ${radius} ${radius} 0 0 1 ${radius} 0`}
                fill="rgba(219,234,254,0.35)" stroke="#3b82f6" strokeWidth={1.5}
            />
            {/* Évidement central */}
            <circle cx={0} cy={0} r={radius * 0.18} fill="rgba(255,255,255,0.6)" stroke="#bfdbfe" strokeWidth={0.8} />
            {ticks}
            {/* Base du rapporteur */}
            <line x1={-radius - 8} y1={0} x2={radius + 8} y2={0} stroke="#3b82f6" strokeWidth={0.8} opacity={0.5} />
            {/* Centre */}
            <circle cx={0} cy={0} r={3} fill="#3b82f6" stroke="#1e40af" strokeWidth={1} />
            <line x1={-8} y1={0} x2={8} y2={0} stroke="#1e40af" strokeWidth={0.8} />
            <line x1={0} y1={-8} x2={0} y2={8} stroke="#1e40af" strokeWidth={0.8} />
            {readLine}
            {/* Poignée de rotation */}
            <circle cx={radius + 16} cy={0} r={13} fill="#dbeafe" stroke="#3b82f6" strokeWidth={1.5} opacity={0.9} />
            <text
                x={radius + 16} y={1} textAnchor="middle" dominantBaseline="central"
                fontSize={13} fill="#3b82f6"
                style={{ pointerEvents: 'none', userSelect: 'none' }}
            >↻</text>
        </g>
    );
};
